<template lang="html">
  <div class="individual-student-ld-summary">
    <div class="top-half flex-column-container">
      <div class="name-and-avatar-container pl-headline">
        <img
          class="student-avatar"
          :src="getStudentSummaryLD.user.avatar_url"
          alt="student avatar"
        />
        {{ getStudentSummaryLD.user.name }}
      </div>
      <div class="row no-margin middle-xs flex-grow">
        <div class="col-xs-6">
          <span
            class="turn-count col-xs-12 row middle-xs center-xs no-margin no-padding"
          >
            {{ total_times_spoken || 0 }}
          </span>
          <span class="times-spoken col-xs-12 no-padding no-margin">
            {{ total_times_spoken === 1 ? 'Time' : 'Times' }} Spoken
          </span>
          <div class="applause-display-container pl-subheading">
            {{ getStudentSummaryLD.applause_count }} x 🙌 received
          </div>
        </div>
        <div class="col-xs-6" style="padding-right: 2em">
          <div
            v-for="(count, type) in hand_raise_types"
            class="row middle-xs center-xs"
          >
            <span class="col-xs-2 pl-bold">{{ `${count}x` }}</span>
            <div class="col-xs-5 center-xs" :class="`${type}-container`">
              <span class="capitalize">{{ type.split('_').join(' ') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Buttons in middle of card -->
    <div class="row no-margin button-row">
      <div
        :class="{ 'tab-selected': selected_tab === 'notes' }"
        @click="selected_tab = 'notes'"
        class="col-xs no-margin left-button"
        style="height: 40px"
      >
        <span>Notes</span>
      </div>
      <div
        :class="{ 'tab-selected': selected_tab === 'feedback' }"
        @click="selected_tab = 'feedback'"
        class="col-xs no-margin right-button"
        style="height: 40px"
      >
        <span>Feedback and Observation</span>
      </div>
    </div>

    <!-- Bottom half with measure components -->
    <div class="start-xs bottom-half" v-if="selected_tab">
      <template v-if="selected_tab === 'notes'">
        <!-- Notes Summary Tab -->
        <div class="content-card">
          <div
            class="pl-subheading row between-xs no-padding"
            style="margin: 0 0 15px 0"
          >
            {{ getStudentSummaryLD.user.name.split(' ')[0] }}'s notes from the
            RoundTable
          </div>
          <div class="pl-body-1 white-spaced-text">
            {{
              // the curly brackets need to be next to tag because of white spacing
              getStudentSummaryLD.notes.content ||
              'The student did not take notes during this activity'
            }}
          </div>
        </div>
      </template>

      <!-- Feedback summary tab -->
      <template v-else-if="selected_tab === 'feedback'">
        <div>
          <live-discussion-points-bar
            :is_summary="true"
            class="col-xs-12 center mb3"
            style="max-width: 400px"
            :participation="participation"
            @saveAssessment="saveAssessment"
          />
          <!-- Teacher observations -->
          <div class="content-card" v-if="hasTeacherPermissionsInCourse">
            <div class="pl-subheading" style="margin-bottom: 15px">
              My observations
              <span class="italics">(private)</span>
            </div>
            <div class="pl-body-1 white-spaced-text">
              {{
                getStudentSummaryLD.observations[0] ||
                'You did not take any notes on this student'
              }}
            </div>
          </div>

          <!-- Nudges from teacher -->
          <div class="content-card">
            <div class="pl-subheading" style="margin-bottom: 15px">
              Nudges from teacher
            </div>
            <div v-if="!getStudentSummaryLD.nudges.length" class="pl-body-1">
              No nudges during this RoundTable
            </div>
            <div
              v-else
              v-for="(nudge, index) in getStudentSummaryLD.nudges"
              :key="index"
              class="pl-body-1"
            >
              {{ nudge.message }}
            </div>
          </div>

          <!-- Feedback to Student -->
          <div class="content-card mv3">
            <div
              class="pl-subheading row middle-xs between-xs"
              style="margin: 0px 0px 15px 0px"
            >
              <div v-if="hasTeacherPermissionsInCourse">
                Feedback
                <span class="italics">
                  ({{ getStudentSummaryLD.user.name.split(' ')[0] }} will see
                  this)
                </span>
              </div>
              <template v-else> Feedback from your teacher </template>
              <a
                class="pl-caption clickable"
                v-if="hasTeacherPermissionsInCourse"
                @click.stop.prevent="editing_feedback = !editing_feedback"
              >
                edit
              </a>
            </div>
            <div
              class="pl-body-1 white-spaced-text"
              v-if="!editing_feedback || !hasTeacherPermissionsInCourse"
            >
              {{
                feedback_for_student || 'There is currently no feedback here...'
              }}
            </div>
            <text-box
              v-else
              @messageSent="handleSendFeedback"
              v-model="feedback_for_student"
              theme="pl-secondary"
              buttonTitle="Submit"
              placeholder="Send Feedback to your student"
            />
          </div>
          <div
            v-if="hasTeacherPermissionsInCourse"
            style="margin-bottom: 20px"
            class="row"
          >
            <div class="col-xs-6">
              <div
                class="suggested-saved-div"
                @click="
                  collapsed_saved_feedback = !collapsed_saved_feedback;
                  collapsed_suggested_feedback = true;
                "
              >
                <span>
                  {{ saved_feedback_collapse_message }}
                </span>
                <i
                  v-if="collapsed_saved_feedback"
                  class="fas fa-arrow-down fa-xs"
                >
                </i>
                <i v-else class="fas fa-arrow-up fa-xs"></i>
              </div>
            </div>

            <div class="col-xs-6">
              <div
                class="suggested-feedback-div"
                @click="
                  collapsed_suggested_feedback = !collapsed_suggested_feedback;
                  collapsed_saved_feedback = true;
                "
              >
                <span>
                  {{ suggested_feedback_collapse_message }}
                </span>
                <i
                  v-if="collapsed_suggested_feedback"
                  class="fas fa-arrow-down fa-xs"
                >
                </i>
                <i v-else class="fas fa-arrow-up fa-xs"></i>
              </div>
            </div>

            <div class="col-xs mt4" v-if="!collapsed_saved_feedback">
              <div class="pl-subheading mb4">
                <div class="center-xs b">Saved Feedback</div>
                <div class="start-xs nt4">
                  <pl-button
                    class="pl-white justify-center"
                    style="
                      margin: 6px 0;
                      line-height: 25px;
                      min-height: 25px;
                      min-width: 60px;
                      border-radius: 5px;
                    "
                    @click.native="show_saved_feedback_modal = true"
                  >
                    <img
                      src="/static/course_assets/create_new_plus_sign.svg"
                      alt=""
                      class="create-new-image"
                    />
                    <span class="f7">New</span>
                  </pl-button>
                  <pl-button
                    class="pl-white"
                    style="
                      line-height: 25px;
                      min-height: 25px;
                      min-width: 60px;
                      border-radius: 5px;
                    "
                    @click.native="show_import_feedback_modal = true"
                  >
                    <span class="f7">Import</span>
                  </pl-button>
                </div>
                <div
                  class="end-xs nt4 pl-body-1 clickable"
                  @click="refreshSavedFeedback"
                >
                  refresh
                </div>
              </div>
              <div v-if="saved_feedback_to_display.length">
                <div
                  class="row middle-xs no-padding mb4"
                  v-for="(feedback, index) in saved_feedback_to_display"
                  :key="index"
                >
                  <div class="no-padding no-margin col-xs-2">
                    <pl-button
                      @click.native="selectSavedFeedback(feedback)"
                      class="pl-icon pl-fog"
                    >
                      <span style="font-weight: bold" class="pl-title">
                        +
                      </span>
                    </pl-button>
                  </div>
                  <div class="no-padding no-margin col-xs-10 tl">
                    {{ feedback.text }}
                    <br />
                    <span
                      @click="editSavedFeedback(feedback)"
                      class="pl-caption clickable"
                    >
                      Edit
                    </span>
                    <span
                      @click="deleteSavedFeedback(feedback)"
                      class="ruby pl-caption clickable"
                    >
                      Delete
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="row">
                <div class="col-xs tc mb4">
                  You have no saved feedback templates. Create a new template or
                  import some from an existing course.
                </div>
              </div>
            </div>

            <div>
              <div class="mt4" v-if="!collapsed_suggested_feedback">
                <div
                  class="pl-subheading mb4 center-xs"
                  style="font-weight: bold"
                >
                  Suggested Feedback
                  <span
                    @click="refreshAutomatedFeedback"
                    style="position: absolute; right: 0px"
                    class="pl-primary clickable pl-body-1"
                  >
                    refresh
                  </span>
                </div>
                <div
                  class="row middle-xs no-padding"
                  style="margin: 0 0 30px 0"
                  v-for="(feedback, index) in automated_feedback_to_display"
                  :key="index"
                >
                  <div class="no-padding no-margin col-xs-2">
                    <pl-button
                      @click.native="selectAutomatedFeedback(feedback)"
                      class="pl-icon pl-fog"
                    >
                      <span style="font-weight: bold" class="pl-title">
                        +
                      </span>
                    </pl-button>
                  </div>
                  <div class="no-padding no-margin col-xs-10 tl">
                    {{ feedback.text }}
                    <br />
                    <span
                      v-my-tooltip="feedback.explanation"
                      class="ruby pl-caption"
                    >
                      Why this suggestion?
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    <saved-feedback-modal
      v-if="show_saved_feedback_modal"
      :feedbackID="feedback_id"
      :feedbackContent="feedback_content"
      :focusOnMounted="true"
      :isEditFeedback="set_edit_saved_feedback"
      :rtType="'live'"
      @modalFeedbackClosed="closeFeedbackModal"
    />
    <import-saved-feedback-modal
      v-if="show_import_feedback_modal"
      @modalFeedbackClosed="show_import_feedback_modal = false"
      :onlineRT="false"
    />
    <!-- Delete online saved feedback -->
    <pl-confirm
      v-if="show_confirm_delete_feedback"
      confirmColor="bg-red-500"
      confirmText="DELETE"
      @close="show_confirm_delete_feedback = false"
      @confirm="confirmDeleteSavedFeedback"
      :message="`Delete this saved feedback template?`"
    >
      <div class="mh4 mb4 tc ph4 pl-subheading">
        <p class="pl-body-1 ma0 italics">
          It will be permanently removed from this course.
        </p>
      </div>
    </pl-confirm>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import StudentBarGraph from './graphs/individualStudentLDGraph';
  import store from 'store';
  import textBox from 'components/textbox';
  import { EventBus } from 'utils/event-bus';
  import SavedFeedbackModal from 'components/modals/savedFeedbackModal';
  import ImportSavedFeedbackModal from 'components/modals/importSavedFeedbackModal';
  import {
    refreshFeedback,
    selectFeedback,
  } from 'services/liveDiscussion/automated_feedback';

  export default {
    data() {
      return {
        data_fetched: false,
        feedback_for_student: '',
        feedback_content: '',
        editing_feedback: false,
        notes_content: '',
        selected_tab: 'feedback',
        notes_published: false,
        taken_suggestions: [],
        collapsed_suggested_feedback: true,
        collapsed_saved_feedback: true,
        show_saved_feedback_modal: false,
        show_import_feedback_modal: false,
        feedback_id: '',
        set_edit_saved_feedback: false,
        show_confirm_delete_feedback: false,
        feedback_to_delete: {},
        saved_feedback: [],
      };
    },
    components: {
      StudentBarGraph,
      textBox,
      SavedFeedbackModal,
      ImportSavedFeedbackModal,
      liveDiscussionPointsBar: require('components/liveDiscussionPointsBar/liveDiscussionPointsBar.vue'),
    },
    watch: {
      /* 'getStudentSummaryLD.participation_id'() {
        this.fetchStudentSummaryData();
      }, */
      getStudentSummary(new_val) {
        if (new_val) {
          this.taken_suggestions = new_val.taken_suggestions;
          this.saved_feedback = new_val.saved_feedback;
        }
      },
    },
    computed: {
      ...mapGetters([
        'getStudentSummaryLD',
        'getStudentSummary',
        'hasTeacherPermissionsInCourse',
        'getCurrentDiscussion',
        'getCourseDetail',
      ]),
      participation() {
        return {
          id: this.getStudentSummaryLD.participation_id,
          rewards: this.getStudentSummaryLD.rewards,
        };
      },
      suggested_feedback_collapse_message() {
        if (this.collapsed_suggested_feedback)
          return (
            'Show Suggested Feedback (' +
            this.automated_feedback_to_display.length +
            ')'
          );
        return (
          'Hide Suggested Feedback (' +
          this.automated_feedback_to_display.length +
          ')'
        );
      },
      saved_feedback_collapse_message() {
        if (this.collapsed_saved_feedback)
          return (
            'Show Saved Feedback (' +
            this.saved_feedback_to_display.length + //change variable
            ')'
          );
        return (
          'Hide Saved Feedback (' +
          this.saved_feedback_to_display.length + //change variable
          ')'
        );
      },
      automated_feedback_to_display() {
        return this.getStudentSummaryLD.automated_feedback.filter(
          (feedback) => {
            return !this.taken_suggestions.includes(feedback.tag);
          }
        );
      },
      saved_feedback_to_display() {
        if (this.saved_feedback && this.saved_feedback.length > 0) {
          return this.getCourseDetail.saved_live_feedback.filter((feedback) => {
            return !this.saved_feedback.includes(feedback.id);
          });
        } else {
          return this.getCourseDetail.saved_live_feedback;
        }
      },
      hand_raise_types() {
        const { new_idea, build_on, challenge, question, hand_raise } =
          this.getStudentSummaryLD.times_spoken;
        const by_teacher = hand_raise;
        return {
          new_idea,
          build_on,
          challenge,
          question,
          by_teacher,
        };
      },
      total_times_spoken() {
        return this.getStudentSummaryLD.times_spoken
          ? this.getStudentSummaryLD.times_spoken.turns_total
          : 0;
      },
      // NOTE: In limbo until we decide what we're going to do with spoken time
      // spoken_time() {
      //   const time = this.getStudentSummaryLD.spoken_time;
      //   const duration = moment.duration(time, 'seconds');
      //   let { seconds, hours, minutes } = duration._data;
      //   if (seconds < 10) {
      //     seconds = `0${seconds}`;
      //   }
      //   if (hours === 0) {
      //     hours = '';
      //   } else if (hours < 10) {
      //     hours = `0${hours}:`;
      //   } else {
      //     hours = `${hours}:`;
      //   }
      //   if (minutes < 10) {
      //     minutes = `0${minutes}`;
      //   }
      //   return `${hours}${minutes}:${seconds}`;
      // },
    },
    methods: {
      handleSendFeedback() {
        this.$store
          .dispatch('submitFeedbackForStudent', {
            participation_id: this.getStudentSummaryLD.participation_id,
            feedback_content: this.feedback_for_student,
          })
          .then(() => {
            this.editing_feedback = false;
          });
      },

      fetchStudentSummaryData() {
        const participationID = this.getStudentSummaryLD.participation_id;
        return store
          .dispatch('fetchParticipationSummary', participationID)
          .then(() => {
            if (this.$route.query.from_assessment) {
              this.selected_tab = 'feedback';
            }
            this.fillParticipationInfo;
          });
      },
      fillParticipationInfo() {
        const feedbackForStudent =
          this.getStudentSummaryLD.feedback_for_student[0];
        if (feedbackForStudent) {
          this.feedback_for_student = feedbackForStudent;
        } else {
          // Text area will be open if there is no saved feedback
          this.editing_feedback = true;
        }
        this.notes_published = this.getStudentSummaryLD.notes.is_published;
      },
      selectSavedFeedback(feedback) {
        this.edit_feedback = true;
        if (this.feedback_for_student) {
          this.feedback_for_student += `\n ${feedback.text}`;
        } else {
          this.feedback_for_student = feedback.text;
        }
        this.$store
          .dispatch('selectSavedFeedbackLD', {
            feedback_id: feedback.id,
            participation_id: this.getStudentSummaryLD.participation_id,
          })
          .then(() => {
            this.saved_feedback.push(feedback.id);
          })
          .then(() => {
            const payload = {
              participation_id: this.getStudentSummaryLD.participation_id,
              feedback_content: this.feedback_for_student,
              auto_save: true,
            };
            this.$store
              .dispatch('submitFeedbackForStudent', payload)
              .then(() => {
                EventBus.$emit('savedEvaluation');
              });
          })
          .catch(console.log);
      },
      refreshAutomatedFeedback() {
        refreshFeedback(this.getStudentSummaryLD.participation_id)
          .then(() => {
            this.taken_suggestions = [];
          })
          .catch(console.log);
      },
      selectAutomatedFeedback(feedback) {
        this.editing_feedback = true;
        if (this.feedback_for_student) {
          this.feedback_for_student += `\n${feedback.text}`;
        } else {
          this.feedback_for_student = feedback.text;
        }
        selectFeedback(this.getStudentSummaryLD.participation_id, {
          tag: feedback.tag,
        })
          .then(() => {
            this.taken_suggestions.push(feedback.tag);
          })
          .then(() => {
            const payload = {
              participation_id: this.getStudentSummaryLD.participation_id,
              feedback_content: this.feedback_for_student,
              auto_save: true,
            };
            this.$store
              .dispatch('submitFeedbackForStudent', payload)
              .then(() => {
                EventBus.$emit('savedEvaluation');
              });
          })
          .catch(console.log);
      },
      refreshSavedFeedback() {
        return (this.saved_feedback = []);
      },
      editSavedFeedback(feedback) {
        this.show_saved_feedback_modal = true;
        this.feedback_id = feedback.id;
        this.feedback_content = feedback.text;
        this.set_edit_saved_feedback = true;
      },
      deleteSavedFeedback(feedback) {
        this.show_confirm_delete_feedback = true;
        this.feedback_to_delete = feedback;
      },
      confirmDeleteSavedFeedback() {
        this.$store
          .dispatch('deleteFeedback', {
            course_id: this.getCourseDetail.id,
            feedback_id: this.feedback_to_delete.id,
            onlineRT: false,
          })
          .then(() => {
            this.feedback_to_delete = {};
            this.show_confirm_delete_feedback = false;
          });
      },
      closeFeedbackModal() {
        this.show_saved_feedback_modal = false;
        this.set_edit_saved_feedback = false;
        this.feedback_id = '';
        this.feedback_content = '';
      },
      saveAssessment(assessment) {
        const assessments = this.mergedAssessments(assessment);
        this.savingAssessment = true;
        this.$store
          .dispatch('updateAssessment', {
            participationID: this.getStudentSummaryLD.participation_id,
            payload: { assessments },
          })
          .then(() => (this.savingAssessment = false))
          .catch(() => (this.savingAssessment = false));
      },
      mergedAssessments(assessment) {
        return this.getStudentSummaryLD.points_settings.peer_rewards.map(
          (pr) => {
            if (assessment.tag === pr.tag) {
              if (
                this.getCurrentDiscussion.point_cap &&
                parseFloat(assessment.value) >
                  this.getCurrentDiscussion.point_cap
              ) {
                pr.value = this.getCurrentDiscussion.point_cap;
              } else {
                pr.value = assessment.value;
              }
            } else {
              const existingAssessment =
                this.getStudentSummaryLD.assessments.find(
                  (a) => a.tag === pr.tag
                );
              if (existingAssessment) {
                if (
                  this.getCurrentDiscussion.point_cap &&
                  parseFloat(existingAssessment.value) >
                    this.getCurrentDiscussion.point_cap
                ) {
                  pr.value = this.getCurrentDiscussion.point_cap;
                } else {
                  pr.value = existingAssessment.value;
                }
              } else {
                pr.value = 0;
              }
            }
            return {
              tag: pr.tag,
              value: pr.value,
            };
          }
        );
      },
    },
    mounted() {
      this.fillParticipationInfo();
      this.fetchStudentSummaryData();
    },
  };
</script>

<style scoped lang="stylus">
  @import '~app/global-stylus.styl'
  .ruby
    color: pl-ruby

  .suggested-feedback-div
    cursor:pointer;
    width:fit-content;
    float:right
    font-size:12px
    margin-top:5px
    &:hover
      color: pl-primary

  .suggested-saved-div
    cursor:pointer;
    width:fit-content;
    float:left
    font-size:12px
    margin-top:5px
    &:hover
      color: pl-primary

  .hand_raise-container, .by_teacher-container,
  .question-container,
  .challenge-container,
  .build_on-container,
  .new_idea-container
    padding: 0px
    font-weight: bold
    margin: 5px 0
  .hand_raise-container, .by_teacher-container
    color: goldenrod
    border: 1px solid goldenrod
  .question-container
    color: #273248
    border: 1px solid #273248
  .challenge-container
    color: pl-ruby
    border: 1px solid pl-ruby
  .build_on-container
    color: green
    border: 1px solid green
  .new_idea-container
    color: pl-fountain-blue
    border: 1px solid pl-fountain-blue

  .applause-display-container
    color: pl-fountain-blue
    margin: 10px 0

  .applause-image
    height: 20px
    width: 20px

  .tab-selected
    background-color: alpha(pl-white-smoke, 0.3)
    border-bottom: 0 !important
    color: pl-charcoal

  .individual-student-ld-summary
    width: 600px
    margin-left: auto
    margin-right: auto
    margin-bottom: 50px
    padding-top: 15px
    border: 1px solid pl-fog
    border-radius: 5px
    background-color: #FFFFFF

    .top-half
      border-radius: 5px 5px 0px 0px
      height: 300px
      background-color: #ffffff
      .top-quarter
        height: calc(100% - 42px)
        padding: 35px 0 0 0
        overflow-y: scroll

      .name-and-avatar-container
        .student-avatar
          box-sizing: border-box;
          border-radius: 50px
          height: 40px;
          width: 40px;
          border: 2px solid alpha(pl-slate, 0.7);

    .bottom-half
      padding: 30px 50px
      border-radius: 0px 0px 5px 5px
      background-color: #fbfbfb
      .content-card
        overflow-wrap: break-word;
        background-color: #FFFFFF
        padding: 20px;
        border: 1px solid pl-fog;
        border-radius: 5px;
        min-height: 100px
        margin-bottom: 10px
        .publish-notes-button
          margin: 0 0 0 30px
          border-radius: 50px
          line-height: 20px
          min-height: 20px
          height: 20px

    .button-row
      border-top: 1px solid pl-fog
      font-weight: bold
      cursor: pointer
      color: #c2c3c4
      .right-button
        padding-top: 9px
        border-left: 1px solid pl-fog
        border-bottom: 1px solid pl-fog
      .left-button
        padding-top: 9px
        border-left: 1px solid pl-fog
        border-bottom: 1px solid pl-fog

    .turn-count
      padding-top:10px
      min-height: 70px
      font-size: 70px
    .times-spoken
      line-height: 35px
      font-size: 25px
</style>



// WEBPACK FOOTER //
// individualStudentLDSummary.vue?14f6c5b3